// frontend/src/pages/players/PlayerForm.jsx

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import teamService from '../../services/teamService';
import playerService from '../../services/playerservice';
import './PlayerForm.css';

function PlayerForm() {
  const { id: playerId } = useParams();
  const navigate = useNavigate();

  const positions = [
    'Goleiro',
    'Zagueiro',
    'Lateral',
    'Volante',
    'Meia',
    'Atacante',
    'Técnico',
    'Auxiliar Técnico'
  ];

  const [teams, setTeams] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    cpf: '',
    // ✅ 1. ADICIONAR O CAMPO AO ESTADO INICIAL
    phone: '',
    birthDate: '',
    position: '',
    shirtNumber: '',
    team: '', 
    photo: null, 
  });
  
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(''); 
  const [existingPhotoUrl, setExistingPhotoUrl] = useState('');
  const [initialPlayerDataForEdit, setInitialPlayerDataForEdit] = useState(null); 

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const year = date.getUTCFullYear();
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      console.error("PlayerForm - Erro ao formatar data para input:", dateString, e);
      return '';
    }
  };

  useEffect(() => {
    setIsLoadingData(true);
    setFormError('');
    setTeams([]);

    const fetchInitialData = async () => {
      let localTeams = [];
      let localError = '';

      try {
        const teamsResponse = await teamService.getAll();
        const teamsDataFromApi = teamsResponse?.data || teamsResponse;
        
        if (Array.isArray(teamsDataFromApi)) {
          localTeams = teamsDataFromApi;
          if (localTeams.length === 0) localError += 'Nenhum time disponível. ';
        } else {
          localError += 'Falha ao carregar times. ';
        }
        setTeams(localTeams);

      } catch (error) {
        localError += 'Erro crítico ao carregar times. ';
        setTeams([]);
      }

      if (playerId) {
        try {
          const playerResponse = await playerService.getById(playerId);
          const playerDataFromApi = playerResponse?.data || playerResponse;

          if (playerDataFromApi && typeof playerDataFromApi === 'object') {
            setInitialPlayerDataForEdit(playerDataFromApi);
            setFormData({
              name: playerDataFromApi.name || '',
              nickname: playerDataFromApi.nickname || '',
              cpf: playerDataFromApi.cpf || '',
              // ✅ 2. ATUALIZAR ESTADO COM O DADO DO JOGADOR
              phone: playerDataFromApi.phone || '',
              birthDate: formatDateForInput(playerDataFromApi.birthDate),
              position: playerDataFromApi.position || '',
              shirtNumber: playerDataFromApi.shirtNumber || '',
              team: playerDataFromApi.team?._id || playerDataFromApi.team || '',
              photo: null, 
            });
            if (playerDataFromApi.photo) {
                setExistingPhotoUrl(playerService.getPlayerPhotoUrl(playerDataFromApi.photo));
            } else {
              setExistingPhotoUrl('');
            }
          } else {
            localError += 'Jogador não encontrado. ';
          }
        } catch (error) {
          localError += (error.response?.data?.message || 'Erro ao buscar jogador.');
        }
      } else {
        // ✅ 3. RESETAR O CAMPO NO FORMULÁRIO DE CRIAÇÃO
        setFormData({ name: '', nickname: '', cpf: '', phone: '', birthDate: '', position: '', shirtNumber: '', team: '', photo: null });
        setExistingPhotoUrl('');
        setInitialPlayerDataForEdit(null);
      }

      if (localError) setFormError(localError.trim());
      setIsLoadingData(false);
    };



    fetchInitialData();
  }, [playerId]); 

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, photo: file })); 

    if (file) {
      setExistingPhotoUrl(URL.createObjectURL(file));
    } else if (initialPlayerDataForEdit?.photo) {
      setExistingPhotoUrl(playerService.getPlayerPhotoUrl(initialPlayerDataForEdit.photo));
    } else {
      setExistingPhotoUrl('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setFormError('');

    const dataToSend = new FormData();
    dataToSend.append('name', formData.name);
    dataToSend.append('nickname', formData.nickname);
    dataToSend.append('cpf', formData.cpf);
    // ✅ 4. ENVIAR O NOVO CAMPO PARA O BACKEND
    dataToSend.append('phone', formData.phone);
    dataToSend.append('birthDate', formData.birthDate);
    dataToSend.append('position', formData.position);
    dataToSend.append('shirtNumber', formData.shirtNumber);
    if(formData.team) dataToSend.append('team', formData.team); 
    
    if (formData.photo instanceof File) { dataToSend.append('photo', formData.photo); }
    
    try {
      if (playerId) {
        await playerService.updatePlayer(playerId, dataToSend);
      } else {
        await playerService.createPlayer(dataToSend);
      }
      navigate('/players'); 
    } catch (err) {
      setFormError(err.response?.data?.message || err.message || 'Ocorreu um erro ao salvar.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="player-form-container">
      <h1 className="form-title">
        {playerId ? 'Editar Jogador' : 'Novo Jogador'}
      </h1>

      {isLoadingData && <p className="form-loading-feedback">Carregando dados...</p>}
      {formError && <p className="form-error-feedback">{formError}</p>}
      
      <form onSubmit={handleSubmit} className="player-form">
        <div>
          <label htmlFor="name" className="form-label">Nome Completo*</label>
          <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="form-input" required />
        </div>

        <div>
          <label htmlFor="nickname" className="form-label">Apelido (Opcional)</label>
          <input type="text" id="nickname" name="nickname" value={formData.nickname} onChange={handleChange} className="form-input" />
        </div>

        <div>
          <label htmlFor="cpf" className="form-label">CPF*</label>
          <input type="text" id="cpf" name="cpf" value={formData.cpf} onChange={handleChange} className="form-input" required />
        </div>
        
        {/* ✅ 5. ADICIONAR O CAMPO DE INPUT NO FORMULÁRIO (JSX) */}
        <div>
          <label htmlFor="phone" className="form-label">Telefone (Opcional)</label>
          <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="form-input" placeholder="(XX) XXXXX-XXXX" />
        </div>

        <div>
          <label htmlFor="birthDate" className="form-label">Data de Nascimento*</label>
          <input type="date" id="birthDate" name="birthDate" value={formData.birthDate} onChange={handleChange} className="form-input" required />
        </div>

        <div>
          <label htmlFor="position" className="form-label">Posição*</label>
          <select id="position" name="position" value={formData.position} onChange={handleChange} className="form-select" required >
            <option value="">Selecione a posição</option>
            {positions.map(pos => (<option key={pos} value={pos}>{pos}</option>))}
          </select>
        </div>

        <div>
          <label htmlFor="shirtNumber" className="form-label">Número da Camisa (Opcional)</label>
          <input type="number" id="shirtNumber" name="shirtNumber" value={formData.shirtNumber} onChange={handleChange} className="form-input" min="0" max="999" />
        </div>

        <div>
          <label htmlFor="team" className="form-label">Time</label>
          <select 
            id="team" 
            name="team" 
            value={formData.team} 
            onChange={handleChange} 
            className="form-select"
            disabled={isLoadingData}
          >
            <option value="">Selecione um time</option>
            {Array.isArray(teams) && teams.map((team) => (
              team && team._id && team.name ? ( <option key={team._id} value={team._id}>{team.name}</option> ) : null
            ))}
          </select>
        </div>
        
        {existingPhotoUrl && (
            <div className="current-photo-preview">
                <p className="form-label">Foto Atual/Preview:</p>
                <img src={existingPhotoUrl} alt="Preview" style={{ maxWidth: '150px', maxHeight: '150px', objectFit: 'cover', marginBottom: '10px', border: '1px solid #ccc' }}/>
            </div>
        )}
        <div>
          <label htmlFor="photoFile" className="form-label">{playerId ? 'Alterar Foto (Opcional)' : 'Foto (Opcional)'}</label>
          <input type="file" id="photoFile" accept="image/*" onChange={handleFileChange} className="form-file-input" />
        </div>

        <button type="submit" className="form-submit-button" disabled={isLoadingData || isSubmitting}>
          {isSubmitting ? (playerId ? 'Atualizando...' : 'Salvando...') 
           : (playerId ? 'Atualizar Jogador' : 'Cadastrar Jogador')}
        </button>
      </form>
    </div>
  );
}

export default PlayerForm;